// Style tokens for the authentication shell (login / register /
// forgot-password pages). Pulled into its own file so the dark
// enterprise look is reusable and the colours stay branding-driven.

export const auth = {
  shell: {
    minHeight: '100vh',
    display: 'flex',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    background: '#f8fafc',
  },

  // ── Left "marketing" panel ────────────────────────────────────────
  // Airbnb-style brand panel: solid purple gradient (no dark navy base)
  // with two soft white orbs as the only decoration. Keeps the
  // marketplace tone consistent with the public landing.
  brandPanel: (gradStart, gradEnd) => ({
    width: '46%',
    minWidth: 0,
    position: 'relative',
    overflow: 'hidden',
    color: '#ffffff',
    background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`,
    padding: '56px clamp(40px, 5vw, 80px)',
    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
  }),
  // Two large soft orbs — Airbnb-style decorative motif (replaces the
  // mesh-dot grid). Rendered as one element via box-shadow so we don't
  // need to add another DOM node.
  brandPanelGrid: {
    position: 'absolute', top: -120, right: -120,
    width: 360, height: 360, borderRadius: '50%',
    background: 'rgba(255,255,255,0.10)',
    boxShadow: '-280px 520px 0 -40px rgba(255,255,255,0.08)',
    pointerEvents: 'none',
  },
  brandHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    position: 'relative',
  },
  brandLogo: (color) => ({
    width: 38, height: 38, borderRadius: 10,
    background: color, color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: '1rem',
  }),
  brandName: { fontSize: '1.25rem', fontWeight: 700, color: '#ffffff', letterSpacing: '0.2px' },

  brandBody: { position: 'relative', marginTop: 64 },
  brandEyebrow: () => ({
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '6px 14px', borderRadius: 999,
    background: 'rgba(255,255,255,0.18)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.25)',
    fontSize: '0.72rem', fontWeight: 700,
    letterSpacing: '0.14em', textTransform: 'uppercase',
    backdropFilter: 'blur(6px)',
  }),
  brandHeadline: {
    fontSize: 'clamp(1.85rem, 2.8vw, 2.6rem)', fontWeight: 800,
    letterSpacing: '-0.025em', color: '#ffffff',
    lineHeight: 1.12, marginTop: 22, marginBottom: 16,
  },
  brandLede: {
    color: 'rgba(255,255,255,0.92)', fontSize: '1.02rem',
    lineHeight: 1.7, marginBottom: 30,
    maxWidth: 480, fontWeight: 400,
  },
  brandBullets: { display: 'flex', flexDirection: 'column', gap: 16 },
  brandBullet: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  // Brighter "earned" check chip — solid white face with the brand
  // tint inside, ringed by a soft halo. Reads as a deliberate proof
  // mark rather than a tonal dot, which the previous translucent
  // circle was bordering on.
  brandBulletDot: (accent) => ({
    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
    background: '#ffffff',
    color: accent,
    boxShadow: '0 0 0 4px rgba(255,255,255,0.18)',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, marginTop: 1,
  }),
  brandBulletText: {
    color: '#ffffff', fontSize: '0.98rem', lineHeight: 1.55, fontWeight: 500,
  },

  // Subtle frosted card on a translucent purple wash. No heavy shadow,
  // no blur — keeps the panel feeling light and marketplace-y.
  brandQuoteCard: {
    position: 'relative',
    marginTop: 28, padding: 22,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.18)',
  },
  brandQuoteText: {
    color: '#ffffff', fontSize: '0.95rem',
    lineHeight: 1.6, marginBottom: 14,
    fontStyle: 'italic', position: 'relative', paddingLeft: 22,
  },
  brandQuoteMark: () => ({
    position: 'absolute', left: 0, top: -8,
    fontSize: '1.8rem', color: '#fff', fontWeight: 800,
  }),
  brandQuoteAuthor: { display: 'flex', alignItems: 'center', gap: 10 },
  brandQuoteAvatar: () => ({
    width: 32, height: 32, borderRadius: '50%',
    background: 'rgba(255,255,255,0.20)',
    color: '#fff', fontWeight: 700, fontSize: '0.8rem',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }),
  brandQuoteAuthorMeta: { color: 'rgba(255,255,255,0.78)', fontSize: '0.83rem' },

  brandStatBand: {
    position: 'relative',
    marginTop: 36, paddingTop: 28,
    borderTop: '1px solid rgba(255,255,255,0.18)',
    display: 'flex', flexWrap: 'wrap', gap: 36,
  },
  brandStatItem: { display: 'flex', flexDirection: 'column', minWidth: 80 },
  // Larger, more confident headline numbers — the band carries social
  // proof, so the values are doing the actual work and deserve weight.
  brandStatValue: () => ({
    fontSize: '1.75rem', fontWeight: 800, color: '#fff',
    letterSpacing: '-0.02em', lineHeight: 1.05,
    fontVariantNumeric: 'tabular-nums',
  }),
  brandStatLabel: {
    color: 'rgba(255,255,255,0.82)', fontSize: '0.72rem', fontWeight: 600,
    letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 6,
  },

  // ── Right "form" panel ────────────────────────────────────────────
  // ``flexDirection: column`` matters: any extra child (e.g. the
  // legacy mobileBrand strip) should stack ABOVE the form, not sit
  // next to it. Padding clamps so phones don't get the desktop's
  // 56px gutter.
  formPanel: {
    flex: 1, minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: 'clamp(16px, 4vw, 56px)',
    background: '#f8fafc',
  },
  formInner: { width: '100%', maxWidth: 460 },

  // Mobile banner (replaces the full left panel below lg).
  // Kept for backwards compatibility — AuthLayout no longer renders
  // it, but other auth pages may still import it.
  mobileBrand: (gradStart, gradEnd) => ({
    background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`,
    color: '#fff',
    padding: '20px 24px',
    display: 'flex', alignItems: 'center', gap: 12,
  }),

  // ── Form card ─────────────────────────────────────────────────────
  // Tighter horizontal clamp on phones — the desktop's 36px gutter
  // halves the usable form width on a 380px viewport. 16px floor
  // leaves the input controls full-width without bumping the edge.
  card: {
    background: '#fff',
    borderRadius: 18,
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.07)',
    padding: 'clamp(20px, 3.2vw, 40px)',
  },
  // Eyebrow now has a slightly stronger tint + a leading icon spot so
  // it reads as a labeled badge rather than a faint tag. Brand colour
  // is mixed at 12% bg / 100% text — same recipe the listing-detail
  // amenity / commodity chips use, keeps the visual language unified.
  cardEyebrow: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '6px 14px', borderRadius: 999,
    background: `${color}14`,
    color,
    border: `1px solid ${color}28`,
    fontSize: '0.72rem', fontWeight: 700,
    letterSpacing: '0.14em', textTransform: 'uppercase',
    marginBottom: 14,
  }),
  cardTitle: {
    fontSize: 'clamp(1.55rem, 2.1vw, 1.8rem)', fontWeight: 800,
    letterSpacing: '-0.02em', color: '#0f172a',
    lineHeight: 1.15, marginBottom: 8,
  },
  cardSub: {
    color: '#64748b', fontSize: '0.95rem',
    lineHeight: 1.6, marginBottom: 24,
  },
  // Trust row reframed as a 3-pill grid: each item gets a small
  // emerald-tinted icon-tile + label. Mirrors the Trust & Safety
  // section on the listing-detail page so users feel like they're
  // moving through the same product, not a generic auth chrome.
  trustRow: {
    marginTop: 24, paddingTop: 18,
    borderTop: '1px solid #f1f5f9',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: 8,
  },
  trustItem: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '8px 10px',
    background: '#ecfdf5',
    border: '1px solid #d1fae5',
    borderRadius: 10,
    color: '#047857',
    fontSize: '0.74rem', fontWeight: 600,
    lineHeight: 1.3,
  },

  // Submit-button helper. The Bootstrap ``Button color="primary"``
  // already paints the brand colour, but it's flat — no hover lift,
  // no glow. Pages can spread these styles onto the button to pick
  // up the same hover-lift + glow shadow used by the homepage CTA.
  formSubmit: (color) => ({
    background: color, borderColor: color,
    boxShadow: `0 6px 18px ${color}40`,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  }),

  divider: {
    margin: '24px 0 18px',
    border: 'none', borderTop: '1px solid #e2e8f0',
  },
  bottomLink: {
    textAlign: 'center', color: '#64748b',
    fontSize: '0.88rem',
  },
};

export default auth;
